import { useEffect, useCallback, useState } from 'react';
import {
    Divider,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    VStack,
    Box,
    Text,
} from '@cad-arc';
import {
    RotationActionEnumType,
    ServiceRotationForm,
    ServiceRotationOption,
    ServiceRotationTypeOption,
    VehicleOption,
} from '@mark43/cad-api';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { ServiceRotationFormType, TowRotationProps } from './types';
import { RotationDisplay } from './rotation-display';
import { ServiceRotationDrawerFooter } from './service-rotation-drawer-footer';
import { useServiceRotationDrawerMutation } from './use-service-rotation-drawer-mutation';
import { useRecommendedServiceProvider } from './use-recommended-service-provider';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { NoServiceProviders } from './no-service-providers';
import { ServiceRotationFormTopSection } from './service-rotation-form-top-section';
import { ManualSelectionSection } from './manual-selection-section';
import { MultiVehicleTowSection } from './multi-vehicle-tow-section';
import { useResetServiceProviderFormOnChange } from './use-reset-service-provider-form-on-change';
import { useForm } from 'react-hook-form';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { useServiceRotationDrawerQuery } from './use-service-rotation-drawer-query';
import { useUpdateServiceRotationForm } from './use-update-service-rotation-form';
import { UseMutationResult } from '@tanstack/react-query';
import { useApplicationType } from '../common/hooks/use-application-type';

export function ServiceRotationDrawerContent({
    eventId,
    towRotationProps,
    defaultServiceRotationTypeId,
}: {
    eventId: number;
    towRotationProps?: TowRotationProps;
    defaultServiceRotationTypeId?: number;
}) {
    const form = useForm<ServiceRotationFormType>({ mode: 'onBlur' });
    const { isFirstResponder } = useApplicationType();
    const isTowRotation = !!towRotationProps;
    const {
        commentsRequiredWhenBypassingServiceRotation,
        enhancedServiceRotation: isEnhancedServiceRotationEnabled,
    } = useEnabledFeatures();
    const [isCommentsRequired, setIsCommentsRequired] = useState<boolean>(false);
    const [serviceTypeOptions, setServiceTypeOptions] = useState<ServiceRotationTypeOption[]>([]);
    const [showMultiVehiclesTowSection, setShowMultiVehiclesTowSection] = useState<boolean>(false);
    const [multiVehiclesOptions, setMultiVehiclesOptions] = useState<VehicleOption[]>([]);
    const [mergedMultiVehiclesOptions, setMergedMultiVehiclesOptions] = useState<VehicleOption[]>(
        []
    );
    const [selectedMultiVehiclesIndexes, setSelectedMultiVehiclesIndexes] = useState<number[]>([]);
    const [selectedMergedMultiVehiclesIndexes, setSelectedMergedMultiVehiclesIndexes] = useState<
        number[]
    >([]);

    const [
        manuallySelectedServiceProvider,
        setManuallySelectedServiceProvider,
    ] = useState<ServiceRotationOption>();
    const isManualSelection = !!manuallySelectedServiceProvider;

    const recommendedServiceProvider = useRecommendedServiceProvider(form);
    const activeServiceProvider = manuallySelectedServiceProvider ?? recommendedServiceProvider;

    useEffect(() => {
        setShowMultiVehiclesTowSection(
            (activeServiceProvider &&
                isEnhancedServiceRotationEnabled &&
                (multiVehiclesOptions.length > 0 || mergedMultiVehiclesOptions.length > 0)) ??
                false
        );
    }, [
        activeServiceProvider,
        isEnhancedServiceRotationEnabled,
        multiVehiclesOptions,
        mergedMultiVehiclesOptions,
    ]);

    useEffect(() => {
        return () => setServiceTypeOptions([]);
    }, []);

    const updateForm = useUpdateServiceRotationForm({
        form,
        defaultServiceRotationTypeId,
        displayedServiceTypeOptions: serviceTypeOptions,
        setDisplayedServiceTypeOptions: setServiceTypeOptions,
        setMultiVehiclesOptions,
        setMergedMultiVehiclesOptions,
    });

    const query = useServiceRotationDrawerQuery({
        form,
        eventId,
        defaultServiceRotationTypeId,
        isTowRotation: !!towRotationProps,
        onSuccess: updateForm,
    });
    const mutation = useServiceRotationDrawerMutation({
        form,
        eventId,
        towRotationProps,
        updateForm,
        selectedMultiVehiclesIndexes,
        selectedMergedMultiVehiclesIndexes,
    });

    useResetServiceProviderFormOnChange(form, setManuallySelectedServiceProvider);

    const submitServiceRotationForm = useCallback(
        (action: RotationActionEnumType) => {
            const formValues = form.getValues();
            if (action === 'ACCEPT') {
                setIsCommentsRequired(false);
            } else if (commentsRequiredWhenBypassingServiceRotation) {
                setIsCommentsRequired(true);
            }
            form.handleSubmit(() => {
                mutation.mutate({
                    serviceProvider: activeServiceProvider!,
                    rotationAction: action,
                    serviceRotationForm: {
                        ...formValues,
                        ...(action === 'ACCEPT' ? { isManualSelection } : {}),
                    } as ServiceRotationForm,
                });
            })();
        },
        [
            form,
            mutation,
            activeServiceProvider,
            commentsRequiredWhenBypassingServiceRotation,
            isManualSelection,
        ]
    );

    const onAccepted = () => submitServiceRotationForm('ACCEPT');
    const onSkip = () => submitServiceRotationForm('SKIP');
    const onNoAnswer = () => submitServiceRotationForm('UNAVAILABLE');
    const onDeclined = () => submitServiceRotationForm('DECLINE');

    return (
        <IntegratedFormProvider
            query={query}
            mutation={
                // safe to cast to match the form because we're only using this mutation for loading behaviors
                (mutation as unknown) as UseMutationResult<
                    void,
                    unknown,
                    ServiceRotationFormType,
                    unknown
                >
            }
            form={form}
        >
            <DrawerContent data-testid={isTowRotation ? 'TOW_ROTATION' : 'SERVICE_ROTATION'}>
                <DrawerHeader>
                    <Text {...(isFirstResponder ? { variant: 'headingLg' } : {})}>
                        {isTowRotation ? 'Tow Rotation' : 'Service Rotation'}
                    </Text>
                </DrawerHeader>
                <DrawerBody>
                    <FormBody>
                        <VStack gap="4px">
                            <ServiceRotationFormTopSection
                                serviceTypeOptions={serviceTypeOptions}
                                isTowRotation={isTowRotation}
                            />
                            <ManualSelectionSection
                                manuallySelectedServiceProvider={manuallySelectedServiceProvider}
                                setManuallySelectedServiceProvider={
                                    setManuallySelectedServiceProvider
                                }
                                recommendedServiceProvider={recommendedServiceProvider}
                            />
                            <Box w="full">
                                <Divider />
                            </Box>
                            {activeServiceProvider ? (
                                <RotationDisplay
                                    isManualSelection={isManualSelection}
                                    isCommentsRequired={isCommentsRequired}
                                    serviceProvider={activeServiceProvider}
                                    onSkip={onSkip}
                                    onNoAnswer={onNoAnswer}
                                    onDeclined={onDeclined}
                                />
                            ) : (
                                <NoServiceProviders />
                            )}
                            {showMultiVehiclesTowSection && (
                                <MultiVehicleTowSection
                                    towRotationProps={towRotationProps}
                                    selectedMultiVehiclesIndexes={selectedMultiVehiclesIndexes}
                                    isLoading={mutation.isLoading}
                                    multiVehiclesOptions={multiVehiclesOptions}
                                    mergedMultiVehiclesOptions={mergedMultiVehiclesOptions}
                                    setSelectedMultiVehiclesIndexes={
                                        setSelectedMultiVehiclesIndexes
                                    }
                                    selectedMergedMultiVehiclesIndexes={
                                        selectedMergedMultiVehiclesIndexes
                                    }
                                    setSelectedMergedMultiVehiclesIndexes={
                                        setSelectedMergedMultiVehiclesIndexes
                                    }
                                />
                            )}
                        </VStack>
                    </FormBody>
                </DrawerBody>
                <DrawerFooter>
                    <ServiceRotationDrawerFooter
                        acceptedDisabled={!activeServiceProvider || mutation.isLoading}
                        onAccepted={onAccepted}
                    />
                </DrawerFooter>
            </DrawerContent>
        </IntegratedFormProvider>
    );
}
